<script setup lang="ts">
// Create 10 slides
import type { SwiperOptions } from "swiper/types"
import type { SwiperContainer } from "swiper/element"

const AUTOPLAY_SPEED = 5000
const containerRef = ref<SwiperContainer | null>(null)

onMounted(async () => {
    // Access Swiper instance
    // Read more about Swiper instance: https://swiperjs.com/swiper-api#methods--properties

    await nextTick() // wait for all slides to be in the DOM

    let resumeTranslate: number | null = null
    let translateBeforeLoopFix: number | null = null

    let params: SwiperOptions = {
        allowTouchMove: true,
        slidesPerView: 2,
        speed: AUTOPLAY_SPEED,
        loop: true,
        centeredSlides: false,
        autoplay: {
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: false
        },
        freeMode: {
            enabled: true,
            momentum: false,
            sticky: false
        },
        breakpoints: {
            768: {
                slidesPerView: 4
            }
        },
        spaceBetween: 50,
        cssMode: false,
        effect: 'slide',
        simulateTouch: false,
        preventClicks: false,
        preventClicksPropagation: false,
        on: {
            touchEnd(swiper) {
                requestAnimationFrame(() => {
                    if (swiper.destroyed) return

                    resumeTranslate = swiper.getTranslate()

                    // Restart even after a quick swipe, before Swiper has marked
                    // autoplay as paused.
                    swiper.autoplay.stop()
                    swiper.animating = false
                    swiper.autoplay.start()
                })
            },
            beforeLoopFix(swiper) {
                if (resumeTranslate !== null) {
                    translateBeforeLoopFix = swiper.translate
                }
            },
            loopFix(swiper) {
                if (resumeTranslate !== null && translateBeforeLoopFix !== null) {
                    // Loop mode can silently shift the whole track. Apply that
                    // same shift to the saved visual starting position.
                    resumeTranslate += swiper.translate - translateBeforeLoopFix
                }

                translateBeforeLoopFix = null
            },
            beforeTransitionStart(swiper, speed) {
                // Ignore loopFix's invisible zero-duration repositioning. The
                // following loopFix event updates resumeTranslate for that shift.
                if (resumeTranslate === null || speed === 0) return

                const targetTranslate = swiper.translate
                const remainingDistance = Math.abs(targetTranslate - resumeTranslate)
                const snapDistances = swiper.snapGrid
                    .slice(1)
                    .map((snap, index) => Math.abs(snap - swiper.snapGrid[index]))
                    .filter(distance => distance > 0)
                    .sort((a, b) => a - b)
                const fullSlideDistance = snapDistances[Math.floor(snapDistances.length / 2)]

                if (fullSlideDistance && Number.isFinite(fullSlideDistance)) {
                    const resumeDuration = Math.max(
                        16,
                        Math.round(AUTOPLAY_SPEED * remainingDistance / fullSlideDistance)
                    )

                    // slideTo has selected its final loop-corrected target now,
                    // so overriding the duration here preserves constant velocity.
                    swiper.setTransition(resumeDuration)
                }

                resumeTranslate = null
            }
        }
    }


    if (!containerRef.value) return
    Object.assign(containerRef.value, params)
    containerRef.value.initialize() // now Swiper sees all slides
})

const { images } = defineProps({
    images: Array as PropType<string[]>
})

const actual_images = () => {
    if (images && images.length < 6) {
        return Array(3)
            .fill([...images])
            .reduce((a, b) => a.concat(b));
    } else {
        return images
    }
}

</script>

<template>
    <ClientOnly>
            <swiper-container
                :init="false"
                ref="containerRef"
            >
                <swiper-slide v-for="(img, idx) in actual_images()" :key="idx">
                    <div class="slide-card">
                        <div class="slide-image">
                            <img :src="`/api/files/image/${img}`" class="object-fit"/>
                        </div>
                    </div>
                </swiper-slide>
            </swiper-container>
    </ClientOnly>
</template>

<style lang="css" scoped>
swiper-container::part(wrapper) {
    transition-timing-function: linear !important;
}

swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 15rem;
    overflow: visible;
    transition-timing-function: linear;
}

swiper-container {
    width: 100%;
    transition-timing-function: linear;
}

.slide-card {
    height: 15rem;
    display: flex;
    flex-direction: column;
    border-radius: 1rem;
    overflow: hidden;
    align-items: center;
    justify-items: center;
}

.slide-image {
    margin-top: 3.5rem;
    min-height: 0;    /* important! prevents flex blowout */
    height: 8rem;
}

.slide-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0.75rem;
    -webkit-user-drag: none;
    user-select: none;
}

.slide-info {
    padding: 0.75rem 0.25rem;
    flex-shrink: 0;
}

.slide-info h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
}

.slide-info p {
    margin: 0.2rem 0 0;
    font-size: 0.875rem;
    color: #888;
}

</style>
